import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import PortalLayout from "@/components/layout/PortalLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import Privacy from "@/pages/Privacy";
import EventDetail from "@/pages/EventDetail";
import Partner from "@/pages/Partner";
import Press from "@/pages/Press";
import Dashboard from "@/pages/portal/Dashboard";
import Profile from "@/pages/portal/Profile";
import Business from "@/pages/portal/Business";
import Directory from "@/pages/portal/Directory";
import HubList from "@/pages/portal/HubList";
import PodcastHub from "@/pages/portal/PodcastHub";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminLocations from "@/pages/admin/AdminLocations";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminStorage from "@/pages/admin/AdminStorage";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminRecordings from "@/pages/admin/AdminRecordings";
import AdminTranscripts from "@/pages/admin/AdminTranscripts";
import AdminNameTags from "@/pages/admin/AdminNameTags";
import RecordingDetail from "@/pages/admin/RecordingDetail";
import ClipEditor from "@/pages/admin/ClipEditor";
import AdminQR from "@/pages/admin/AdminQR";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminEmail from "@/pages/admin/AdminEmail";
import SharedClips from "@/pages/portal/SharedClips";
import MemberCard from "@/pages/portal/MemberCard";
import Rewards from "@/pages/portal/Rewards";
import AccountHub from "@/pages/portal/AccountHub";
import HelpHub from "@/pages/portal/HelpHub";
import GuideArticle from "@/pages/portal/GuideArticle";
import PreShowChecklist from "@/pages/portal/PreShowChecklist";
import StudioHub from "@/pages/admin/StudioHub";
import SocialStudio from "@/pages/admin/SocialStudio";
import StudioHome from "@/pages/studio/StudioHome";
import ProducerConsole from "@/pages/studio/ProducerConsole";
import ProgramOutput from "@/pages/studio/ProgramOutput";
import RoomMonitor from "@/pages/monitor/RoomMonitor";
import GuestJoin from "@/pages/join/GuestJoin";
import CameraJoin from "@/pages/join/CameraJoin";
import CoreVoiceRoom from "@/pages/portal/CoreVoiceRoom";
import ManageHub from "@/pages/admin/ManageHub";
import PublicClip from "@/pages/PublicClip";
import PublicMember from "@/pages/PublicMember";
import Onboarding from "@/pages/Onboarding";
import InviteMember from "@/pages/portal/InviteMember";

function AppRoutes() {
  // Subscribe to the router location so this component re-renders on navigation.
  // The OAuth provider returns the session_id as a URL hash; we intercept it
  // reactively (not via a one-time window.location read) so that navigating away
  // from the callback screen — e.g. "Back to home" — re-renders correctly.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/events/:eventId" element={<EventDetail />} />
      <Route path="/partner" element={<Partner />} />
      <Route path="/press" element={<Press />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/clip/:token" element={<PublicClip />} />
      <Route path="/m/:token" element={<PublicMember />} />

      {/* Studio v2 — public join links + program output (recorder page) */}
      <Route path="/join/:token" element={<GuestJoin />} />
      <Route path="/cam/:token" element={<CameraJoin />} />
      <Route path="/program/:showId/:key" element={<ProgramOutput />} />
      <Route path="/monitor/:showId/:key" element={<RoomMonitor />} />
      {/* Studio v2 — full-screen producer console (outside the portal chrome) */}
      <Route path="/studio/shows/:showId" element={<ProtectedRoute requireCap="producer_tools"><ProducerConsole /></ProtectedRoute>} />

      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      <Route
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/portal" element={<Dashboard />} />
        <Route path="/studio" element={<ProtectedRoute requireCap="producer_tools"><StudioHome /></ProtectedRoute>} />
        <Route path="/portal/directory" element={<Directory />} />
        <Route path="/portal/hub" element={<HubList />} />
        <Route path="/portal/hub/:sessionId" element={<PodcastHub />} />
        <Route path="/portal/clips" element={<SharedClips />} />
        <Route path="/portal/rewards" element={<Rewards />} />
        <Route path="/portal/invite" element={<InviteMember />} />
        <Route path="/portal/voice" element={<ProtectedRoute requireCap="view_members"><CoreVoiceRoom /></ProtectedRoute>} />

        {/* Help & Guides hub — list, printable checklist (producer-gated), then article viewer */}
        <Route path="/portal/guides" element={<HelpHub />} />
        <Route path="/portal/guides/checklist" element={<ProtectedRoute requireCap="producer_tools"><PreShowChecklist /></ProtectedRoute>} />
        <Route path="/portal/guides/:slug" element={<GuideArticle />} />

        {/* My Account hub — Card / Business / Profile combined into tabs */}
        <Route path="/portal/account" element={<AccountHub />}>
          <Route index element={<Navigate to="card" replace />} />
          <Route path="card" element={<MemberCard />} />
          <Route path="business" element={<Business />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        {/* Legacy redirects → My Account hub */}
        <Route path="/portal/card" element={<Navigate to="/portal/account/card" replace />} />
        <Route path="/portal/business" element={<Navigate to="/portal/account/business" replace />} />
        <Route path="/portal/profile" element={<Navigate to="/portal/account/profile" replace />} />

        {/* Producer Studio hub — Live Show / Recordings / Videos / Mixer */}
        <Route path="/admin/studio" element={<ProtectedRoute requireCap="producer_tools"><StudioHub /></ProtectedRoute>}>
          <Route index element={<Navigate to="recordings" replace />} />
          <Route path="recordings" element={<AdminRecordings />} />
          <Route path="transcripts" element={<AdminTranscripts />} />
          <Route path="videos" element={<AdminVideos />} />
          <Route path="name-tags" element={<AdminNameTags />} />
          <Route path="social" element={<SocialStudio />} />
        </Route>
        {/* Recording detail + clip editor stay full-width focus views */}
        <Route path="/admin/recordings/:id" element={<ProtectedRoute requireCap="producer_tools"><RecordingDetail /></ProtectedRoute>} />
        <Route path="/admin/recordings/:id/clips" element={<ProtectedRoute requireCap="producer_tools"><ClipEditor /></ProtectedRoute>} />
        {/* Legacy redirects → Studio hub */}
        <Route path="/admin/recordings" element={<Navigate to="/admin/studio/recordings" replace />} />
        <Route path="/admin/videos" element={<Navigate to="/admin/studio/videos" replace />} />
        {/* Legacy production routes -> new Studio */}
        <Route path="/admin/mixer" element={<Navigate to="/studio" replace />} />
        <Route path="/admin/studio/live" element={<Navigate to="/studio" replace />} />
        <Route path="/admin/studio/record" element={<Navigate to="/studio" replace />} />
        <Route path="/admin/studio/mixer" element={<Navigate to="/studio" replace />} />
        <Route path="/admin/studio/green-room" element={<Navigate to="/studio" replace />} />

        {/* Manage hub — Events / Locations / Media / Storage / QR / Inquiries */}
        <Route path="/admin/manage" element={<ProtectedRoute requireCap="manage_events"><ManageHub /></ProtectedRoute>}>
          <Route index element={<Navigate to="events" replace />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="locations" element={<AdminLocations />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="storage" element={<AdminStorage />} />
          <Route path="qr" element={<AdminQR />} />
          <Route path="inquiries" element={<AdminInquiries />} />
          <Route path="email" element={<ProtectedRoute requireCap="manage_members"><AdminEmail /></ProtectedRoute>} />
        </Route>
        {/* Legacy redirects → Manage hub */}
        <Route path="/admin/events" element={<Navigate to="/admin/manage/events" replace />} />
        <Route path="/admin/locations" element={<Navigate to="/admin/manage/locations" replace />} />
        <Route path="/admin/media" element={<Navigate to="/admin/manage/media" replace />} />
        <Route path="/admin/storage" element={<Navigate to="/admin/manage/storage" replace />} />
        <Route path="/admin/qr" element={<Navigate to="/admin/manage/qr" replace />} />
        <Route path="/admin/inquiries" element={<Navigate to="/admin/manage/inquiries" replace />} />

        {/* Leadership — kept top-level */}
        <Route path="/admin/members" element={<ProtectedRoute requireCap="view_members"><AdminMembers /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute requireCap="view_analytics"><AdminAnalytics /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster richColors position="top-right" />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
